"use client";

import { cn } from "utils";
import {
  AGGREGATED_ONBOARDING_CTA_CLASS,
  AGGREGATED_ONBOARDING_FOOTER_CLASS,
  AGGREGATED_ONBOARDING_SCROLL_CLASS,
  AGGREGATED_ONBOARDING_STEP_COLUMN_CLASS,
  AGGREGATED_ONBOARDING_STEP_ROOT_CLASS,
} from "../AggregatedFlow/aggregatedOnboardingLayoutTokens";
import { PortfolioInsightErrorCircleIcon } from "./PortfolioInsightErrorCircleIcon";
import { PortfolioInsightAlertCard } from "./PortfolioInsightAlertCard";
import { PortfolioInsightMeetEdHeader } from "./PortfolioInsightMeetEdHeader";
import { PortfolioInsightNumberedCard } from "./PortfolioInsightNumberedCard";
import { PORTFOLIO_INSIGHT_MOCK } from "./portfolioInsightMock";
import type { PortfolioInsightOnboardingModalContentProps } from "./types";

/**
 * ⑦ 诊断页 UI。既可用于独立 `openPortfolioInsightOnboardingModal`，
 * 也可作为聚合流 `AggregatedOnboardingFlowContent` 的第二步子视图。
 *
 * 布局：Meet Ed 固定顶栏；headline / 列表 / notice 在中间滚动区；CTA 固定底栏。
 */
export const PortfolioInsightOnboardingModalContent: React.FC<
  PortfolioInsightOnboardingModalContentProps
> = ({
  /** Host 无参打开独立弹窗时走 MOCK；聚合流传入 generate 映射后的 payload */
  payload = PORTFOLIO_INSIGHT_MOCK,
  layout = "standalone",
  onClose,
}) => {
  const { headerTitle, headline, noticeLine, insights, ctaLabel } = payload;
  const isAggregated = layout === "aggregated";

  const contentColumnClass = cn(
    "flex flex-col px-4",
    isAggregated
      ? cn(AGGREGATED_ONBOARDING_SCROLL_CLASS, "md:px-6")
      : "min-h-0 flex-1 flex-col overflow-y-auto md:mx-auto md:w-full md:max-w-[600px] md:px-6",
  );

  const footerClass = isAggregated
    ? AGGREGATED_ONBOARDING_FOOTER_CLASS
    : "shrink-0 px-4 py-6 pb-safe md:mx-auto md:w-full md:max-w-[600px] md:px-6 md:py-6 md:pb-6";

  const ctaClass = cn(
    isAggregated
      ? cn(AGGREGATED_ONBOARDING_CTA_CLASS, "mb-3 md:mb-0")
      : "button button_m md_button primary_button flex h-11 w-full items-center justify-center rounded-2 md:w-auto md:min-w-[80px] md:px-4",
  );

  return (
    <div
      className={cn(
        isAggregated
          ? AGGREGATED_ONBOARDING_STEP_ROOT_CLASS
          : "flex h-full w-full flex-col bg-background_solid_secondary pt-safe",
      )}
      data-testid="portfolio-insight-modal-view"
      data-layout={layout}
    >
      <div
        className={cn(
          isAggregated
            ? AGGREGATED_ONBOARDING_STEP_COLUMN_CLASS
            : "flex min-h-0 flex-1 flex-col md:h-auto md:flex-none",
        )}
        data-testid="portfolio-insight-compare-root"
      >
        <header
          className="shrink-0 px-4 pb-3 pt-2 md:px-6 md:pt-4"
          data-testid="portfolio-insight-meet-ed-header-block"
        >
          <PortfolioInsightMeetEdHeader title={headerTitle} />
        </header>

        <div
          className={contentColumnClass}
          data-testid="portfolio-insight-scroll-view"
        >
          <div className="flex flex-col gap-6 md:gap-6">
            <p
              className="headline_regular_s text-content_primary"
              data-testid="portfolio-insight-headline-view"
            >
              {headline}
            </p>

            {noticeLine ? (
              <div
                className="hidden items-center gap-1 md:flex"
                data-testid="portfolio-insight-notice-view"
              >
                <PortfolioInsightErrorCircleIcon
                  variant="notice"
                  data-testid="portfolio-insight-error-icon-notice"
                />
                <p
                  className="body_output text-content_primary"
                  data-testid="portfolio-insight-notice-text"
                >
                  {noticeLine}
                </p>
              </div>
            ) : null}

            <div className="flex flex-col gap-4 md:gap-3">
              {insights.map((item, index) => (
                <div key={item.id} className="contents">
                  <PortfolioInsightAlertCard
                    item={item}
                    className="md:hidden"
                    data-testid={`portfolio-insight-card-${index}-view`}
                  />
                  <PortfolioInsightNumberedCard
                    index={index + 1}
                    item={item}
                    className="hidden md:flex"
                    data-testid={`portfolio-insight-card-${index}-view`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <footer className={footerClass}>
          <button
            type="button"
            data-openreplay-label="PortfolioInsight · See the trackers"
            data-testid="portfolio-insight-cta-click"
            className={ctaClass}
            onClick={() => onClose("view")}
          >
            {ctaLabel}
          </button>
        </footer>
      </div>
    </div>
  );
};
